import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button';
import Api, {Utils} from '../../Api';
import {formatPrice, findWithToken} from './tools';

import BagItem from './BagItem';

import Bag from '../../images/icons/purse.svg';

export default class Flyout extends React.Component {
  static propTypes = {
    isAdminImpersonating: PropTypes.bool, /* if true, ignore brand minimums */
    closeCart: PropTypes.func,
    checkout: PropTypes.func,
    disableEditing: PropTypes.bool
  };

  constructor(props) {
    super(props);

    Utils.bindThis(this, [
      'updateCart',
      'emptyCart',
      'changeQuantity',
      'checkout',
      'onKeyDown',
      'belowMinimum',
      'checkoutComputedButtonText'
    ]);

    this.state = {
      cart: null,
      parsed_items: null,
      products: null,
      shake: false,
      editingDisabled: props.disableEditing === true,
      brandOrder: []
    };

    this.shakeTimer = null;
  }

  componentWillMount() {
    document.addEventListener("keydown", this.onKeyDown);
    this.updateCart(this.props.cart)
  }

  componentWillReceiveProps(newProps) {
    // Props received. Update state.

    this.updateCart(newProps.cart);

    let editingDisabled = newProps.disableEditing === true;
    if (editingDisabled !== this.state.editingDisabled) {
      this.setState({editingDisabled});
    }
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.onKeyDown);
    if (this.shakeTimer) clearTimeout(this.shakeTimer);
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && Api.cache.getCartVisible()) {
      Api.cache.setCartVisible(false);
      event.preventDefault();
    }
  }

  /* If we don't have anything on the cart, just display an empty cart. */
  emptyCart() {
    return (
      <div className="cart-contents empty-cart">
        <span className="clickcatcher" onClick={this.props.closeCart}/>
        <span className="outer-wrapper">
          <span className="inner-wrapper">
            <span className="header-wrapper"><h2>Your Bag</h2></span>
            <div className="cart-products-wrapper">
              <div className="shopping-cart-icon"><img role="presentation" src={Bag}/></div>
              <div>You have no items in your bag</div>
            </div>
          </span>
        </span>
      </div>
    );
  }

  updateCart(cartData) {
    if (!cartData) {
      this.setState({cart: null});
      return;
    }

    let cart = Utils.clone(cartData);

    cart.brandOrder = cart.parsed_items
      .map(brand => brand.token)
      .sort((aToken, bToken) => {
        let brandA = findWithToken(cart.parsed_items, aToken);
        let brandB = findWithToken(cart.parsed_items, bToken);
        return brandA.name.localeCompare(brandB.name);
      });

    cart.checkoutText = this.checkoutComputedButtonText();
    this.setState(cart);
  }

  // Sets the quantity of a specific cart item
  changeQuantity(token, quantity) {
    let parsed_items = Utils.clone(this.state.parsed_items, true);

    // find cart item
    let chosenItem = null;
    parsed_items.forEach((brand) => {
      brand.products.forEach((cart_product) => {
        if (cart_product.token === token) chosenItem = cart_product;
      })
    });

    if (!chosenItem) {
      console.error('error: cannot find product with token %s on cart.', token);
      return;
    }
    chosenItem.loading = true;
    chosenItem.quantity = quantity;

    this.setState({parsed_items});

    Api.addToCart([chosenItem]);
  }

  checkout(event) {
    let belowMinimum = this.state.parsed_items.filter((brand) => {
      return (brand.total_cents < brand.minimum_order_amount_cents);
    });

    if (belowMinimum.length <= 0 || this.props.isAdminImpersonating) {
      this.props.checkout();
      return;
    }

    this.setState({
      shake: true
    });

    if (!this.shakeTimer) {
      this.shakeTimer = setTimeout(
        () => {
          this.setState({shake: false});
          this.shakeTimer = null;
        },
        3000
      );
    }
  }

  belowMinimum() {
    if (!this.state.parsed_items) return [];

    return this.state.parsed_items.filter((brand) => {
      return (brand.total_cents < brand.minimum_order_amount_cents);
    });
  }

  checkoutComputedButtonText() {
    if (this.shakeTimer) {
      return 'Some Orders Below minimum';
    }

    if (this.belowMinimum().length > 0) {
      return 'Some Orders Below minimum';
    }

    return 'Proceed to Checkout';
  }

  render() {
    let cart = this.state.cart;

    if (!cart || cart.items.length === 0 || !this.state.parsed_items || this.state.parsed_items.length === 0) {
      return (this.emptyCart());
    }

    // Creates one block for each brand...
    let itemsDisplay = this.state.brandOrder
      .map(brandToken => findWithToken(this.state.parsed_items, brandToken))
      .map(brand =>
        <BrandSection
          retailerToken={this.props.retailerToken}
          key={"cart-" + brand.token}
          brand={brand}
          products={this.state.products}
          changeQuantity={this.changeQuantity}
          disableEditing={this.state.editingDisabled}
        />
      );

    let belowMinimum = this.belowMinimum();

    // If we have a checkout prop, we display the button action bar. If not, do not.
    let buttonActionBar = null;
    if (this.props.checkout && typeof(this.props.checkout) === 'function') {
      let classNames = [];
      if (this.state.shake) classNames.push('shake');
      if (belowMinimum.length > 0) classNames.push('disabled');

      buttonActionBar =
        <div className="actions">
          <Button className={classNames.join(' ')} onClick={this.checkout}>
            {this.checkoutComputedButtonText()}
            <span className="button-subtotal">${Utils.formatCents(this.state.cart.total_value_cents)}</span>
          </Button>
        </div>;
    }

    let discount = this.state.cart.discount;

    let subtotal = null;
    if (this.props.subtotal) {
      let discountView = null;
      if (discount && discount.code && discount.total_discount_amount_cents) {
        discountView = <div className="promo-code-discount">
          <h3>{`Promo Code: ${discount.code}`}</h3>
          <span className="amount">${Utils.formatCents(discount.total_discount_amount_cents)}</span>
        </div>
      }

      let shipping = (discount && discount.free_shipping)
        ? "FREE"
        : "TBD*";

      subtotal =
        <div className="cart-summary">
          <div className="subtotal">
            <h3>Subtotal</h3>
            <span className="amount">${Utils.formatCents(this.state.cart.total_value_cents)}</span>
          </div>
          {discountView}
          <div className="shipping-cost">
            <h3>Shipping</h3>
            <span className="amount">{shipping}</span>
          </div>
        </div>;
    }

    let textTotal = null;
    if (this.props.textTotal) {
      let totalCents = (discount && discount.total_amount_cents)
        ? discount.total_amount_cents
        : this.state.cart.total_value_cents;
      textTotal =
        <div className="text-total">
          <h3>Total</h3>
          <span className="amount">${Utils.formatCents(totalCents)}</span>
        </div>
    }

    return (
      <div className={'cart-contents' + (belowMinimum.length === 0 ? '' : ' below-minimum')}>
        <span className="clickcatcher" onClick={this.props.closeCart}><div className="background"/></span>
        <span className="outer-wrapper">
          <span className="inner-wrapper">
            <div className="header-wrapper">
              <h2>Your Bag</h2>
            </div>
            <div className="cart-products-wrapper">
              <div className="cart-products">{itemsDisplay}</div>
            </div>
            <div className="fix-bottom">
              {subtotal}
              {buttonActionBar}
              {textTotal}
            </div>
          </span>
        </span>
      </div>
    );
  }
}

class BrandSection extends React.Component {
  static propTypes = {
    brand: PropTypes.object.isRequired,
    products: PropTypes.object.isRequired,
    changeQuantity: PropTypes.func.isRequired,
    disableEditing: PropTypes.bool
  };

  static contextTypes = {
    router: PropTypes.object
  };

  constructor(props) {
    super(props);

    Utils.bindThis(this, ['brandNameClick']);

    this.state = {
      collapsed: false
    };
  }

  brandNameClick(event) {
    event.preventDefault();

    if (!this.props.retailerToken) return;

    let url = `/retailer/${this.props.retailerToken}/brand/${this.props.brand.token}`;
    Api.cache.setCartVisible(false);
    this.context.router.push(url);
  }

  render() {
    let brand = this.props.brand;
    let belowMinimumAlert = null;
    let brandQuantity = 0;
    let brandCents = 0;

    let productBlocks = [];
    brand.products
      .forEach(item => {
        let productBlock = productBlocks.find(block => item.product_token === block.product_token);
        if (!productBlock) {
          productBlock = {
            product_token: item.product_token,
            product: this.props.products[item.product_token],
            quantity: 0,
            price_cents: 0,
            item_list: []
          };
          productBlocks.push(productBlock);
        }
        productBlock.quantity += item.quantity;
        productBlock.price_cents += item.quantity * item.price_cents;
        productBlock.item_list.push(item);
      });

    productBlocks = productBlocks.sort((blockA, blockB) => blockA.product.name.localeCompare(blockB.product.name));

    // one block for each item.
    let products = productBlocks.map(block => {
      brandQuantity += block.quantity;
      brandCents += block.price_cents;
      return <BagItem disableEditing={this.props.disableEditing} key={block.product.token} itemList={block.item_list}
                      product={block.product} retailerToken={this.props.retailerToken}/>;
    });

    if (brand.below_minimum) {
      belowMinimumAlert = <div className="below-minimum-alert link" onClick={this.brandNameClick}>
        Add {formatPrice(brand.minimum_order_amount_cents - brand.total_cents)} to reach {brand.name}
        {' '}Minimum &gt;</div>;
    }

    let brandTitle = (
      <h2 onClick={this.brandNameClick} className={!!this.props.retailerToken ? 'link' : ''}>
        <span className="quantity">{brandQuantity}</span>
        <span className="brand-name">{brand.name}</span>
        <span className="value">${Utils.formatCents(brandCents)}</span>
      </h2>
    );

    return (
      <div className={"brand-section" + ((belowMinimumAlert !== null) ? ' below-minimum' : '')}
           key={"cart-" + brand.token}>
        {brandTitle}
        {belowMinimumAlert}
        <div className="products">{products}</div>
      </div>
    );
  }
}



/** WEBPACK FOOTER **
 ** ./src/components/Cart/flyout.js
 **/